import { useQuery } from "@tanstack/react-query";
import {
  reportsApi,
  type ReportType,
  type ReportsQueryFilters,
  type ReportsResponseMap,
} from "../services/reportsApi";

const normalizeFilters = (filters: ReportsQueryFilters): ReportsQueryFilters => {
  const normalized: Record<string, string> = {};

  Object.entries(filters).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") {
      normalized[key] = value;
    }
  });

  return normalized as ReportsQueryFilters;
};

const fetchByType = async <T extends ReportType>(
  reportType: T,
  filters: ReportsQueryFilters
): Promise<ReportsResponseMap[T]> => {
  switch (reportType) {
    case "production-costs":
      return reportsApi.getProductionCostsReport(filters) as Promise<
        ReportsResponseMap[T]
      >;
    case "input-costs":
      return reportsApi.getInputCostsReport(filters) as Promise<
        ReportsResponseMap[T]
      >;
    case "material-usage":
      return reportsApi.getMaterialUsageReport(filters) as Promise<
        ReportsResponseMap[T]
      >;
    case "profitability-summary":
      return reportsApi.getProfitabilitySummaryReport(filters) as Promise<
        ReportsResponseMap[T]
      >;
    case "cost-comparison":
      return reportsApi.getCostComparisonReport(filters) as Promise<
        ReportsResponseMap[T]
      >;
    case "order-profitability":
      if (!filters.orderId) {
        throw new Error("Order ID is required for order profitability report");
      }
      return reportsApi.getOrderProfitabilityReport(filters.orderId) as Promise<
        ReportsResponseMap[T]
      >;
    case "payment-summary":
      return reportsApi.getPaymentSummaryReport(filters) as Promise<
        ReportsResponseMap[T]
      >;
    case "revenue-summary":
      return reportsApi.getRevenueSummaryReport(filters) as Promise<
        ReportsResponseMap[T]
      >;
    case "revenue-by-period":
      return reportsApi.getRevenueByPeriodReport(filters) as Promise<
        ReportsResponseMap[T]
      >;
    case "revenue-by-customer":
      return reportsApi.getRevenueByCustomerReport(filters) as Promise<
        ReportsResponseMap[T]
      >;
    case "revenue-by-product":
      return reportsApi.getRevenueByProductReport(filters) as Promise<
        ReportsResponseMap[T]
      >;
    case "revenue-ar-summary":
      return reportsApi.getRevenueAccountsReceivableSummaryReport(filters) as Promise<
        ReportsResponseMap[T]
      >;
    default:
      throw new Error("Unsupported report type");
  }
};

export const useReportQuery = <T extends ReportType>(
  reportType: T,
  filters: ReportsQueryFilters,
  runToken: number
) => {
  const normalizedFilters = normalizeFilters(filters);

  const { pageNumber, pageSize, groupPageNumber, groupPageSize, ...reportFilters } = normalizedFilters;
  const paging = reportType === "material-usage" ? { pageNumber, pageSize, groupPageNumber, groupPageSize } : undefined;
  return useQuery({
    queryKey: ["reports", reportType, reportFilters, paging],
    queryFn: () => fetchByType(reportType, normalizedFilters),
    enabled: runToken > 0,
    refetchOnWindowFocus: false,
    placeholderData: (previous, previousQuery) =>
      reportType === "material-usage" && previousQuery?.queryKey[1] === reportType &&
      JSON.stringify(previousQuery.queryKey[2]) === JSON.stringify(reportFilters) ? previous : undefined,
  });
};
